import { BODY_GROUND_OFFSET } from "./lpc/body_placement"

/** Ground footprints in tile-local pixels. Sprite height includes the raised wall face;
 * it is deliberately not the footprint. These profiles use zero-based atlas positions. */
export interface TileBlockingRect {
    x: number
    y: number
    width: number
    height: number
    /** Clearance removed from the approaching body's bottom edge for this obstacle.
     * Allows approach from the north without shifting the southern contact boundary. */
    bodyInsetBottom?: number
    /** Support boundaries constrain soles instead of the taller body collision box. */
    groundSupport?: boolean
}

export const CaveArtSetBackTop = new Set([344, 345, 346])
export const validateBlockingRects = (rects: unknown): TileBlockingRect[] => {
    if (!Array.isArray(rects) || !rects.length || rects.some(rect => !rect ||
        ![rect.x, rect.y, rect.width, rect.height].every(Number.isFinite) ||
        rect.width <= 0 || rect.height <= 0 || rect.x < -32 || rect.y < -32 ||
        (rect.bodyInsetBottom !== undefined && (!Number.isFinite(rect.bodyInsetBottom) ||
            rect.bodyInsetBottom < 0 || rect.bodyInsetBottom > 32)) ||
        rect.x + rect.width > 64 || rect.y + rect.height > 64)) {
        throw new Error("blockingRects must contain positive finite rectangles within one tile of the parent cell")
    }
    return rects
}
/** Decorative corner continuations have solid ground only beneath their matching blocking cliff. */
export const caveCliffFootprint = (position: number, parentPosition: number, parentBlocking: boolean): TileBlockingRect[] => {
    if (!parentBlocking || (position !== 440 && position !== 442) ||
        parentPosition !== (position === 440 ? 408 : 410)) return undefined
    return [{ x: position === 440 ? 16 : 0, y: 0, width: 16, height: 32 }]
}

export const CaveArtSetBackLeft = new Set([344, 376, 408, 440, 472])
export const CaveArtSetBackRight = new Set([346, 378, 410, 442, 474])

const wood = {
    "fence_medieval": [441, 426, 425, 427, 495, 473, 475, 412, 431, 396, 424, 456, 474],
    "walls2": [107, 76, 75, 77, 209, 171, 173, 46, 81, 14, 74, 138, 172],
}
const woodCaps = {
    "fence_medieval": [408, 409, 410, 411, 440, 444, 445, 457, 458, 459, 490, 492, 493, 506, 508],
    "walls2": [42, 43, 44, 45, 106, 110, 111, 139, 140, 141, 204, 206, 207, 236, 238],
}

export const woodenWallVariant = (tilesetId: string, position: number): number =>
    woodCaps[tilesetId]?.includes(position) ? 13 : wood[tilesetId]?.indexOf(position) ?? -1

export const woodenWallFootprint = (variant: number, northConnected: boolean): TileBlockingRect[] => {
    const vertical = { x: 12, y: 0, width: 8, height: 32 }
    // The horizontal wall's ground plane sits toward the north of its base tile.
    // This lets bodies approach beneath the raised face without exposing feet behind it.
    const horizontal = { x: 0, y: 4, width: 32, height: 8 }
    const right = { x: 12, y: 4, width: 20, height: 8 }
    const left = { x: 0, y: 4, width: 20, height: 8 }
    const north = { x: 12, y: 0, width: 8, height: 12 }
    const south = { x: 12, y: 4, width: 8, height: 28 }
    switch (variant) {
        case 0: return [vertical]
        case 1: return northConnected ? [horizontal, north] : [horizontal]
        case 12: return [horizontal, northConnected ? vertical : south]
        case 2: return [right, northConnected ? vertical : south]
        case 3: case 4: return [left, northConnected ? vertical : south]
        case 5: return [north, right]
        case 6: return [north, left]
        case 7: case 8: return [{ x: 12, y: 12, width: 8, height: 20 }]
        case 9: return [{ x: 12, y: 0, width: 8, height: 20 }]
        case 10: return [right]
        case 11: return [left]
        default: return []
    }
}

/** Keep a positive forward half even for bodies smaller than the authored inset. */
export const tileProfileBodyBottom = (y1: number, y2: number, rect: TileBlockingRect): number =>
    rect.groundSupport ? (y1 + y2) / 2 + BODY_GROUND_OFFSET : y2 - Math.min(rect.bodyInsetBottom || 0, Math.max(0, (y2 - y1) / 2 - 0.5))

export const tileProfileBodyTop = (y1: number, y2: number, rect: TileBlockingRect): number =>
    rect.groundSupport ? (y1 + y2) / 2 + BODY_GROUND_OFFSET : y1

export const blockingRectOverlaps = (
    x1: number, y1: number, x2: number, y2: number, rect: TileBlockingRect,
) => (rect.groundSupport ? (x1 + x2) / 2 < rect.x + rect.width && (x1 + x2) / 2 > rect.x
    : x1 < rect.x + rect.width && x2 > rect.x) && tileProfileBodyTop(y1, y2, rect) < rect.y + rect.height &&
    tileProfileBodyBottom(y1, y2, rect) > rect.y
